import { ReactNode } from 'react';

/**
 * Validation result for a value
 */
export interface ValidationResult {
  valid: boolean;
  errors?: string[];
  warnings?: string[];
}

/**
 * DataType interface encapsulates all logic for handling a specific type of data
 * including formatting, rendering, sorting, styling, filtering, and editing.
 */
export interface DataType<T = unknown> {
  /**
   * Render the value as a ReactNode
   * @param value - The raw value from the accessor
   * @param rowIndex - The row index (optional, for context)
   */
  render(value: T, rowIndex?: number): ReactNode;

  /**
   * Compare two values for sorting
   * @param a - First value
   * @param b - Second value
   * @returns negative if a < b, 0 if equal, positive if a > b
   */
  compare(a: T, b: T): number;

  /**
   * Optional: Get cell style based on value
   * @param value - The value
   * @param rowIndex - The row index
   */
  getCellStyle?(value: T, rowIndex?: number): React.CSSProperties;

  /**
   * Optional: Alignment hint for the column
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Optional: Indicates if this DataType supports discrete value filtering
   * If true, the column can use a multi-select filter showing all unique values
   */
  supportsDiscreteValues?: boolean;

  /**
   * Optional: Get a display label for a value (used in filter UI)
   * Defaults to render() if not provided
   */
  getFilterLabel?(value: T): string;

  /**
   * Optional: Render an edit control for inline editing
   * @param value - Current value
   * @param onChange - Callback when value changes
   * @param onCommit - Callback to commit the edit
   * @param onCancel - Callback to cancel the edit
   */
  renderEditor?(
    value: T,
    onChange: (newValue: T) => void,
    onCommit: () => void,
    onCancel: () => void
  ): ReactNode;

  /**
   * Optional: Parse string input to typed value (for simple text input)
   * @param input - String input from user
   * @returns Parsed value or undefined if invalid
   */
  parseInput?(input: string): T | undefined;

  /**
   * Optional: Format value as string for editing (inverse of parseInput)
   * @param value - Value to format
   * @returns String representation for editing
   */
  formatForEdit?(value: T): string;

  /**
   * Optional: Validate a value
   * @param value - Value to validate
   * @returns ValidationResult with errors/warnings
   */
  validate?(value: T): ValidationResult;

  /**
   * Optional: The value a cell holds when it is empty but not NULL ("" for text,
   * 0 for a number). Absent, the type has no empty value and a cell flips only to NULL.
   */
  emptyValue?: T;
}

/** How a button or menu item names a type's empty value: "" for the empty string. */
export function emptyValueLabel(value: unknown): string {
  return value === '' ? '""' : String(value);
}

/**
 * Helper function to create a valid ValidationResult
 */
export function validResult(): ValidationResult {
  return { valid: true };
}

/**
 * Helper function to create an invalid ValidationResult
 */
export function invalidResult(errors: string[], warnings?: string[]): ValidationResult {
  return { valid: false, errors, warnings };
}

/**
 * String data type
 */
export class StringDataType implements DataType<string> {
  constructor(private options?: {
    align?: 'left' | 'center' | 'right';
    discreteValues?: boolean;
    required?: boolean;
    nullable?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternMessage?: string;
    /** the column's empty value; null when it has none (a uuid or timestamp held as text) */
    emptyValue?: string | null;
  }) {}

  get emptyValue(): string | undefined {
    return this.options?.emptyValue === undefined ? '' : this.options.emptyValue ?? undefined;
  }

  render(value: string): ReactNode {
    if (value === null || value === undefined) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>∅</span>;
    }
    return value;
  }

  compare(a: string, b: string): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    // Coerce to string if not already (handles numbers, etc.)
    const aStr = String(a);
    const bStr = String(b);

    return aStr.localeCompare(bStr);
  }

  get align() {
    return this.options?.align || 'left';
  }

  get supportsDiscreteValues() {
    return this.options?.discreteValues ?? false;
  }

  getFilterLabel(value: string): string {
    return value ?? '';
  }

  parseInput(input: string): string | undefined {
    return input;
  }

  formatForEdit(value: string): string {
    return value ?? '';
  }

  renderEditor(
    value: string,
    onChange: (newValue: string) => void,
    onCommit: () => void,
    onCancel: () => void
  ): ReactNode {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onCommit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={onCommit}
        autoFocus
        style={{
          width: '100%',
          padding: '4px 8px',
          border: '2px solid #4a6fa5',
          backgroundColor: '#1a1a1a',
          color: '#e0e0e0',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  validate(value: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if non-nullable column has null/undefined value
    if (this.options?.nullable === false && (value === null || value === undefined)) {
      errors.push('This field cannot be NULL');
    }

    if (this.options?.required && (!value || value.trim().length === 0)) {
      errors.push('This field is required');
    }

    if (value && this.options?.minLength && value.length < this.options.minLength) {
      errors.push(`Minimum length is ${this.options.minLength} characters`);
    }

    if (value && this.options?.maxLength && value.length > this.options.maxLength) {
      errors.push(`Maximum length is ${this.options.maxLength} characters`);
    }

    if (value && this.options?.pattern && !this.options.pattern.test(value)) {
      errors.push(this.options.patternMessage || 'Invalid format');
    }

    return errors.length > 0 ? invalidResult(errors, warnings) : validResult();
  }
}

/**
 * Number data type
 */
export class NumberDataType implements DataType<number> {
  constructor(private options?: {
    align?: 'left' | 'center' | 'right';
    decimals?: number;
    locale?: string;
    discreteValues?: boolean;
    required?: boolean;
    nullable?: boolean;
    min?: number;
    max?: number;
    integer?: boolean;
  }) {}

  readonly emptyValue = 0;

  render(value: number): ReactNode {
    if (value == null) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>∅</span>;
    }
    if (this.options?.decimals !== undefined) {
      return value.toFixed(this.options.decimals);
    }
    return value.toLocaleString(this.options?.locale);
  }

  compare(a: number, b: number): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  }

  get align() {
    return this.options?.align || 'right';
  }

  get supportsDiscreteValues() {
    return this.options?.discreteValues ?? false;
  }

  getFilterLabel(value: number): string {
    return this.render(value) as string;
  }

  parseInput(input: string): number | undefined {
    if (!input || input.trim().length === 0) return undefined;
    const num = Number(input);
    return isNaN(num) ? undefined : num;
  }

  formatForEdit(value: number): string {
    return value != null ? String(value) : '';
  }

  renderEditor(
    value: number,
    onChange: (newValue: number) => void,
    onCommit: () => void,
    onCancel: () => void
  ): ReactNode {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onCommit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '' && !e.target.validity.badInput) {
        onChange(this.emptyValue);
        return;
      }
      const parsed = this.parseInput(e.target.value);
      if (parsed !== undefined) {
        onChange(parsed);
      }
    };

    return (
      <input
        type="number"
        defaultValue={this.formatForEdit(value)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={onCommit}
        autoFocus
        step={this.options?.decimals !== undefined ? Math.pow(10, -this.options.decimals) : 1}
        style={{
          width: '100%',
          padding: '4px 8px',
          border: '2px solid #4a6fa5',
          backgroundColor: '#1a1a1a',
          color: '#e0e0e0',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  validate(value: number): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if non-nullable column has null/undefined value
    if (this.options?.nullable === false && value == null) {
      errors.push('This field cannot be NULL');
      return invalidResult(errors, warnings);
    }

    if (this.options?.required && value == null) {
      errors.push('This field is required');
      return invalidResult(errors, warnings);
    }

    if (value != null) {
      if (this.options?.integer && !Number.isInteger(value)) {
        errors.push('Value must be an integer');
      }

      if (this.options?.min !== undefined && value < this.options.min) {
        errors.push(`Value must be at least ${this.options.min}`);
      }

      if (this.options?.max !== undefined && value > this.options.max) {
        errors.push(`Value must be at most ${this.options.max}`);
      }
    }

    return errors.length > 0 ? invalidResult(errors, warnings) : validResult();
  }
}

/**
 * Currency data type
 */
export class CurrencyDataType implements DataType<number> {
  constructor(private options?: {
    symbol?: string;
    locale?: string;
    decimals?: number;
  }) {}

  readonly emptyValue = 0;

  render(value: number): ReactNode {
    if (value == null) return '';
    const symbol = this.options?.symbol || '$';
    const formatted = value.toLocaleString(this.options?.locale, {
      minimumFractionDigits: this.options?.decimals ?? 0,
      maximumFractionDigits: this.options?.decimals ?? 0,
    });
    return `${symbol}${formatted}`;
  }

  compare(a: number, b: number): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  }

  get align() {
    return 'right' as const;
  }
}

/**
 * Byte size data type (formats as KB, MB, GB, etc.)
 */
export class ByteSizeDataType implements DataType<number> {
  constructor(private options?: { decimals?: number }) {}

  readonly emptyValue = 0;

  render(value: number): ReactNode {
    if (value == null || value === 0) return '0 Bytes';

    const k = 1024;
    const dm = this.options?.decimals ?? 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(value) / Math.log(k));

    return parseFloat((value / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  compare(a: number, b: number): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  }

  get align() {
    return 'right' as const;
  }
}

/**
 * Date data type
 */
export class DateDataType implements DataType<Date | string | number> {
  constructor(private options?: {
    format?: 'short' | 'medium' | 'long' | 'full';
    locale?: string;
  }) {}

  render(value: Date | string | number): ReactNode {
    if (value == null) return '';
    const date = value instanceof Date ? value : new Date(value);

    if (isNaN(date.getTime())) return '';

    const locale = this.options?.locale || 'en-US';
    const format = this.options?.format || 'medium';

    const formatOptionsMap: Record<string, Intl.DateTimeFormatOptions> = {
      short: { year: 'numeric', month: 'numeric', day: 'numeric' },
      medium: { year: 'numeric', month: 'short', day: 'numeric' },
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    };
    const formatOptions = formatOptionsMap[format];

    return date.toLocaleDateString(locale, formatOptions);
  }

  compare(a: Date | string | number, b: Date | string | number): number {
    const dateA = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const dateB = b instanceof Date ? b.getTime() : new Date(b).getTime();

    if (isNaN(dateA)) return 1;
    if (isNaN(dateB)) return -1;

    return dateA - dateB;
  }

  get align() {
    return 'left' as const;
  }
}

/**
 * Percentage data type
 */
export class PercentageDataType implements DataType<number> {
  constructor(private options?: {
    decimals?: number;
    multiplier?: number; // if value is 0.5 and multiplier is 100, shows as 50%
  }) {}

  readonly emptyValue = 0;

  render(value: number): ReactNode {
    if (value == null) return '';
    const multiplier = this.options?.multiplier ?? 1;
    const decimals = this.options?.decimals ?? 1;
    const percent = value * multiplier;
    return `${percent.toFixed(decimals)}%`;
  }

  compare(a: number, b: number): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  }

  get align() {
    return 'right' as const;
  }
}

/**
 * Boolean data type
 */
export class BooleanDataType implements DataType<boolean> {
  constructor(private options?: {
    trueText?: string;
    falseText?: string;
    align?: 'left' | 'center' | 'right';
  }) {}

  readonly emptyValue = false;

  render(value: boolean): ReactNode {
    if (value == null) return '';
    const trueText = this.options?.trueText ?? 'Yes';
    const falseText = this.options?.falseText ?? 'No';
    return value ? trueText : falseText;
  }

  compare(a: boolean, b: boolean): number {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a ? -1 : 1; // true comes before false
  }

  get align() {
    return this.options?.align || 'center';
  }
}
